import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles, AlertOctagon, AlertCircle, AlertTriangle, TrendingDown, TrendingUp,
  ShieldAlert, Target, ChevronRight, CheckCircle2, ArrowRight, Megaphone, Clock, FlaskConical, Video,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ComunicadoCompositor from "@/components/ComunicadoCompositor";

const ICON_MAP = {
  "alert-octagon": AlertOctagon,
  "alert-circle": AlertCircle,
  "warning": AlertTriangle,
  "trending-down": TrendingDown,
  "trending-up": TrendingUp,
  "shield-alert": ShieldAlert,
  "target": Target,
  "clock": Clock,
  "flask": FlaskConical,
  "video": Video,
};

const SEV_THEME = {
  critica: { label: "Crítica", color: "#DC2626" },
  alta:    { label: "Alta",    color: "#E08A1F" },
  media:   { label: "Media",   color: "#C9A24A" },
  baja:    { label: "Aviso",   color: "#1E5BCC" },
};

/**
 * Insights inteligentes basados en deltas y umbrales financieros.
 * Renderiza una sección compacta justo arriba del Centro de alertas
 * con tarjetas accionables (DSO, facturación, productividad, etc).
 */
export default function SmartInsights() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [compose, setCompose] = useState(null); // { prefill } | null
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    let mounted = true;
    const load = () => api.get("/dashboard/smart_alerts")
      .then((r) => { if (mounted) setData(r.data); })
      .catch(() => { if (mounted) setData({ alerts: [], count: 0 }); });
    load();
    const t = setInterval(load, 60_000); // auto-refresco cada 60s
    return () => { mounted = false; clearInterval(t); };
  }, []);

  if (!data) return null;
  const alerts = data.alerts || [];

  // Estado "todo OK" — pequeña tarjeta verde elegante
  if (alerts.length === 0) {
    return (
      <motion.section
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-6 md:mb-8"
        data-testid="smart-insights"
      >
        <div
          className="flex items-center gap-3 rounded-2xl px-5 py-4"
          style={{
            background: "linear-gradient(135deg, #0F8A5F10 0%, var(--legatum-surface) 60%)",
            border: "1.5px solid #0F8A5F66",
            boxShadow: "0 12px 26px -14px #0F8A5F55",
          }}
        >
          <div
            className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0"
            style={{ background: "#0F8A5F1F", border: "1px solid #0F8A5F55" }}
          >
            <CheckCircle2 className="h-5 w-5" style={{ color: "#0F8A5F" }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--legatum-text-muted)] font-bold flex items-center gap-1.5">
              <Sparkles className="h-3 w-3 text-[var(--legatum-accent)]" /> Insights IA
            </div>
            <div className="mt-0.5 text-sm font-semibold text-[var(--legatum-text)]">
              Todo bajo control — no se detectan tendencias preocupantes.
            </div>
          </div>
        </div>
      </motion.section>
    );
  }

  return (
    <motion.section
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-6 md:mb-8"
      data-testid="smart-insights"
    >
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-lg flex items-center justify-center bg-[var(--legatum-accent)]/15 text-[var(--legatum-accent)] border border-[var(--legatum-accent)]/30">
            <Sparkles className="h-3.5 w-3.5" />
          </div>
          <h2 className="text-sm font-extrabold uppercase tracking-[0.2em] text-[var(--legatum-text-muted)]">
            Insights inteligentes · IA
          </h2>
          <span
            className="ml-1 inline-flex items-center justify-center min-w-[20px] h-[20px] px-1.5 rounded-full text-[10px] font-bold tabular-nums"
            style={{
              background: "var(--legatum-accent)",
              color: "var(--legatum-accent-fg)",
            }}
            data-testid="smart-insights-count"
          >
            {alerts.length}
          </span>
        </div>
        <div className="text-[10px] text-[var(--legatum-text-muted)] uppercase tracking-widest font-bold hidden md:block">
          Análisis basado en deltas · auto-refresco 60s
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="smart-insights-grid">
        <AnimatePresence mode="popLayout">
          {alerts.map((a, i) => {
            const sev = SEV_THEME[a.severidad] || SEV_THEME.baja;
            const Icon = ICON_MAP[a.icon] || AlertCircle;
            return (
              <motion.div
                key={a.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.3, delay: i * 0.05 }}
                layout
              >
                <Link
                  to={a.ruta || "/"}
                  data-testid={`smart-insight-${a.kpi}`}
                  className="group relative flex items-start gap-3 p-4 pr-3 rounded-xl transition-all hover:-translate-y-0.5"
                  style={{
                    background: `linear-gradient(135deg, ${sev.color}0F 0%, var(--legatum-surface) 55%)`,
                    border: `1.5px solid ${sev.color}55`,
                    boxShadow: `0 10px 22px -14px ${sev.color}55`,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = `${sev.color}AA`;
                    e.currentTarget.style.boxShadow = `0 16px 30px -14px ${sev.color}88`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = `${sev.color}55`;
                    e.currentTarget.style.boxShadow = `0 10px 22px -14px ${sev.color}55`;
                  }}
                >
                  {/* Acento lateral con pulse para críticas */}
                  <span
                    aria-hidden
                    className="absolute left-0 top-3 bottom-3 w-[3px] rounded-r-full"
                    style={{ background: sev.color, boxShadow: `0 0 8px ${sev.color}88` }}
                  />

                  <div
                    className="relative h-10 w-10 rounded-lg flex items-center justify-center shrink-0"
                    style={{
                      background: `${sev.color}1F`,
                      border: `1px solid ${sev.color}55`,
                    }}
                  >
                    {a.severidad === "critica" && (
                      <span className="absolute inset-0 rounded-lg animate-ping opacity-40" style={{ background: `${sev.color}55` }} />
                    )}
                    <Icon className="h-5 w-5 relative" style={{ color: sev.color }} />
                  </div>

                  <div className="relative flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                      <span
                        className="rounded-md uppercase text-[9px] tracking-wider font-bold px-1.5 py-0.5"
                        style={{ background: `${sev.color}1F`, color: sev.color, border: `1px solid ${sev.color}55` }}
                      >
                        {sev.label}
                      </span>
                    </div>
                    <div className="text-sm md:text-base font-bold text-[var(--legatum-text)] leading-snug">
                      {a.titulo}
                    </div>
                    <div className="mt-1 text-xs text-[var(--legatum-text-secondary)] leading-snug">
                      {a.detalle}
                    </div>
                    {a.accion && (
                      <div className="mt-2 flex items-center gap-1 text-[11px] font-semibold" style={{ color: sev.color }}>
                        <ArrowRight className="h-3 w-3" />
                        <span>{a.accion}</span>
                      </div>
                    )}
                  </div>

                  <div className="relative flex flex-col items-end gap-2 shrink-0 self-stretch">
                    {isAdmin && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const meta = a.meta || {};
                          const isDocobra = String(a.kpi || "").startsWith("docobra_");
                          const hasResp = (meta.responsable_ids || []).length > 0;
                          // Construir contenido enriquecido para alertas docobra
                          const lines = [`Aviso automático del sistema de Insights inteligentes.`, ``, `▸ ${a.titulo}`, ``, a.detalle];
                          if (isDocobra && Array.isArray(meta.items) && meta.items.length) {
                            lines.push(``, `Hitos afectados:`);
                            meta.items.slice(0, 8).forEach((it) => {
                              const dias = it.dias < 0 ? `${Math.abs(it.dias)}d atrasado` : `vence en ${it.dias}d`;
                              lines.push(`  · ${it.label} — ${dias}${it.ref_legal ? ` (${it.ref_legal})` : ""}${it.responsable ? ` · resp: ${it.responsable}` : ""}`);
                            });
                          }
                          if (meta.tipo === "calidad_pendiente" && Array.isArray(meta.items) && meta.items.length) {
                            lines.push(``, `Compras sin ensayo de calidad:`);
                            meta.items.slice(0, 8).forEach((it) => {
                              const imp = (it.importe || 0).toLocaleString("es-ES", { style: "currency", currency: "EUR" });
                              lines.push(`  · ${it.label} — ${imp}${it.proveedor ? ` · ${it.proveedor}` : ""}${it.fecha ? ` · ${it.fecha}` : ""}`);
                            });
                          }
                          lines.push(``, `Acción recomendada: ${a.accion}`);
                          if ((isDocobra || meta.tipo === "calidad_pendiente") && meta.obra_id) {
                            const origin = typeof window !== "undefined" ? window.location.origin : "";
                            lines.push(``, `Cockpit documental: ${origin}${a.ruta}`);
                          }
                          lines.push(``, `Por favor revisa la información y confirma que se está atendiendo.`, ``, `— LEGATUM CAPITAL SAAS · Notificación`);
                          setCompose({
                            prefill: {
                              destinoTipo: hasResp ? (meta.responsable_ids.length > 1 ? "multiple" : "individual") : "all",
                              destIds: hasResp ? meta.responsable_ids : [],
                              asunto: `[Alerta IA] ${a.titulo}`,
                              contenido: lines.join("\n"),
                              urgencia: a.severidad === "critica" ? "muy_alta" : (a.severidad === "alta" ? "alta" : "normal"),
                              requiere_acuse: a.severidad === "critica" || a.severidad === "alta",
                              requiere_firma: false,
                            },
                          });
                        }}
                        data-testid={`smart-insight-${a.kpi}-notificar`}
                        title="Crear comunicado interno sobre esta alerta"
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-widest border transition-colors hover:scale-105"
                        style={{
                          background: `${sev.color}14`,
                          color: sev.color,
                          borderColor: `${sev.color}55`,
                        }}
                      >
                        <Megaphone className="h-3 w-3" /> Notificar
                      </button>
                    )}
                    <ChevronRight className="h-4 w-4 text-[var(--legatum-text-muted)] group-hover:translate-x-1 group-hover:text-[var(--legatum-accent)] transition-all" />
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      {compose && (
        <ComunicadoCompositor
          prefill={compose.prefill}
          onClose={() => setCompose(null)}
          onSent={() => setCompose(null)}
        />
      )}
    </motion.section>
  );
}
